import { useEffect, useMemo, useState } from "react";
import { toCardImages } from "../../lib/cardImage";
import { getProjectCardImages, type Project } from "../../lib/projects";

const AUTOPLAY_MS = 2200;

type ProjectCardSlideshowProps = {
  project: Project;
  alt: string;
  className?: string;
  /** When false, only the cover is shown (no hover carousel). Better for LCP-heavy pages. */
  enableHoverSlideshow?: boolean;
  /** First visible cards: eager + high fetch priority for LCP. */
  priority?: boolean;
};

export function ProjectCardSlideshow({
  project,
  alt,
  className = "w-full h-48 rounded-t-xl",
  enableHoverSlideshow = true,
  priority = false,
}: ProjectCardSlideshowProps): JSX.Element {
  const images = useMemo(
    () => toCardImages(getProjectCardImages(project)),
    [project],
  );
  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const hasMultiple = enableHoverSlideshow && images.length > 1;
  const activeSrc = images[index] ?? images[0];

  useEffect(() => {
    if (!hovering || !hasMultiple) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [hovering, hasMultiple, images.length]);

  const handleEnter = () => {
    if (!hasMultiple) return;
    setHovering(true);
    setIndex(0);
  };

  const handleLeave = () => {
    setHovering(false);
    setIndex(0);
  };

  if (!activeSrc) {
    return <div className={`relative overflow-hidden bg-gray-100 ${className}`} />;
  }

  return (
    <div
      className={`relative overflow-hidden bg-gray-100 ${className}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <img
        src={activeSrc}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding={priority ? "sync" : "async"}
        width={800}
        height={500}
      />
      {hovering && hasMultiple && images[(index + 1) % images.length] !== activeSrc ? (
        <img
          src={images[(index + 1) % images.length]}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none"
          loading="lazy"
          decoding="async"
        />
      ) : null}

      {hasMultiple && hovering && (
        <div
          className="absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5 pointer-events-none"
          aria-hidden
        >
          {images.map((_, i) => (
            <span
              key={i}
              className={`block h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-4 bg-white shadow-sm" : "w-1.5 bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
